/**
 * Main Entry Point
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [REFACTOR] EventBus + ViewerAPI — убраны race conditions window.MikhmelViewer
 * [GOD-TIER] Integrated MemoryGuardian & Smart DatasetManager
 */

// ══════════════════════════════════════════════════════════════
// [FIX] AUTO CACHE BUST — clears SW cache when a new build is detected.
// Uses import.meta.url which contains the Vite-generated hash.
// ══════════════════════════════════════════════════════════════
try {
  const _buildMatch = import.meta.url.match(/index-([A-Za-z0-9_-]+)\.js/);
  const _buildHash = _buildMatch ? _buildMatch[1] : '';
  if (_buildHash) {
    const _CACHE_KEY = 'mikhmel_build_hash';
    const _storedHash = localStorage.getItem(_CACHE_KEY);
    if (_storedHash && _storedHash !== _buildHash) {
      console.log(`[CacheBust] Build changed: ${_storedHash} → ${_buildHash}. Clearing caches...`);
      localStorage.setItem(_CACHE_KEY, _buildHash);
      // [12.0.20] .catch у ОБОИХ. try/catch вокруг ловит только синхронный
      // бросок, а это обещания: в настольной оболочке страница живёт по схеме
      // app://, и getRegistrations() отказывает с InvalidStateError («The
      // document is in an invalid state»). Отказ был никем не пойман и
      // попадал в журнал врача как [CRITICAL] при каждом запуске новой сборки.
      //
      // В настольной сборке служебного потока нет вовсе (его ставит только
      // веб-сборка, см. pwaInstall), поэтому там и спрашивать не о чем.
      if ('caches' in window) {
        caches.keys().then(n => n.forEach(k => caches.delete(k))).catch(() => { /* кеша нет — и не надо */ });
      }
      const _настольная = (typeof __BUILD_MODE__ !== 'undefined' && __BUILD_MODE__ === 'desktop')
        || !!window.MikhmelDesktop?.isDesktop;
      if (!_настольная && 'serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations()
          .then(r => r.forEach(w => w.unregister()))
          .catch(() => { /* страница не управляется служебным потоком */ });
      }
    } else if (!_storedHash) {
      localStorage.setItem(_CACHE_KEY, _buildHash);
    }
  }
} catch (e) { /* non-critical */ }

// [RESTORE] Бывшие инлайн-скрипты index.html (вынесены ради CSP). В оригинале
// подключались отдельными <script>-тегами; модульных импортов не имеют, поэтому
// без явного импорта сборщик их выкидывает — и умирают меню, шторки и хоткеи.
import './ui/htmlInit.js';
import './ui/mobileBindings.js';
import { initCornerstone } from './core/init.js';
import { initUI } from './ui/uiManager.js';
import { initScreenFit } from './ui/screenFit.js';   // [10.0.2] реальная высота видимой области
import { checkExpiry, expiryMessage, verifyUnlock, unlockKeyFor, действующийСрок, EXPIRY_UTC, СРОК_ЗАПИСАН_ВЕРНО } from './core/buildExpiry.js';   // [10.1.4] срок годности сборки
import { ErrorBoundary, setupGlobalErrorHandlers } from './utils/errorBoundary.js';   // [10.0.3] общая ловушка исключений
import { checkBrowserSupport, showToast, escapeHtml } from './utils/helpers.js';
import { decideRenderMode, describeRenderMode } from './core/renderMode.js';   // [10.4.0] на что способна эта машина
import { showStartupDiagnostic, hookFileInputs } from './core/gpuDetector.js';
import { emit } from './core/eventBus.js';
import { installGlobal, registerAPIs } from './core/viewerAPI.js';
import { разложитьРазделы } from './ui/enterprisePanel.js'; // [10.19.47] панели нет — разделы живут в сайдбаре и окнах
import { startAutoRotate, stopAutoRotate, isAutoRotating } from './core/viewportCamera.js';
import { find3DViewportId } from './core/viewportState.js';
import { initIndexBindings } from './ui/indexBindings.js';
import { initOblique } from './ui/obliqueBindings.js';   // [10.19.11] косые реформаты
import { initCPR } from './ui/cprPicker.js';   // [10.19.11] развёртка сосуда по двум точкам
import { initQuantTools } from './ui/quantTools.js'; // [10.19.14] CAD-RADS и карты T1/T2
import { initPetPerfusionTools } from './ui/petPerfusionTools.js'; // [12.0.2] ПЭТ SUV, перфузия КТ, ДКУ Ktrans
import { initWorklist } from './ui/worklistUI.js'; // [12.0.5] рабочий список по QIDO
import { initLvEf } from './ui/lvEfTools.js';   // [10.19.24] ФВ по маске ЛЖ (кино)
import { initEchoReg } from './ui/echoRegTools.js';   // [10.19.24] совмещение эхо-кадров
import { initEchoSimpson } from './ui/echoSimpsonTools.js';   // [12.0.42] ФВ по Симпсону на эхо-кино

// [NEW] Импортируем God-Tier модули
import { memoryGuardian } from './managers/MemoryGuardian.js';
import { datasetManager } from './managers/DatasetManager.js';

// [REFACTOR] Extracted inline JS modules
import { initApp } from './ui/appInit.js';
import { initMobileTopbar } from './ui/mobileTopbar.js';
import { initViewportResize } from './ui/viewportResize.js';
import { initMobileNav } from './ui/mobileNav.js';
import { initPWA } from './ui/pwaInstall.js';
import { initSidebarResize } from './ui/sidebarResize.js';
import { initContextMenu } from './ui/contextMenu.js';
import { applyProfileToUI, showProfileSelector, loadProfile, setProfile } from './ui/profileManager.js';
import { stopAutoSpin } from './core/viewportEffects.js'; // [10.19.31] мост: кнопочное вращение глушит выключатель-автовращение
import { initInlineHelp } from './ui/inlineHelp.js';
import { initMobileStability } from './ui/mobileStability.js';
import { initProFeaturesBindings } from './ui/proFeaturesBindings.js';
import { initPremiumFeatures } from './ui/premiumFeatures.js';
import { initRTStruct } from './io/rtStructModule.js';
import { initRegistration } from './registration/rtRegisterModule.js';
import { initRTDose } from './io/rtDoseModule.js';
import { initDVH } from './io/rtDvhModule.js';
import { initIsocenter } from './io/rtIsocenterModule.js';
import { initProtocolGate } from './io/rtProtocolGateModule.js';
import { initCredits } from './ui/credits.js';
import { initRecoveryReport } from './ui/recoveryReport.js';
// [9.0.0] Спрайт SVG-иконок. Файл лежал в дереве, но его не подключал никто —
// то есть весь набор иконок существовал вхолостую.
import { injectIconSprite } from './core/iconManager.js';
// [9.0.0] Выгрузку вкладки целиком мы раньше только называли. Теперь предлагаем вернуться.
import { initSessionRestore } from './ui/sessionRestore.js';
import { log } from './utils/logger.js';

const VERSION = (typeof __APP_VERSION__ !== 'undefined') ? __APP_VERSION__ : '9.0.4';
const APP_NAME = 'Mikhmel Cornerstone4D DICOM Viewer Pro';
// [NEW] Определяем режим сборки
const BUILD_MODE = (typeof __BUILD_MODE__ !== 'undefined') ? __BUILD_MODE__ : 'web';
const IS_DESKTOP_BUILD = BUILD_MODE === 'desktop';

// Константы DOM элементов
const DOM_IDS = {
  LOADER: 'app-loader',
  APP: 'app'
};

// Определение окружения (может приходить из сборщика, например Vite/Webpack)
const IS_DEV = window.location.hostname === 'localhost' || window.location.search.includes('debug=true');

// ─── Install Proxy API FIRST — до любого кода ───
installGlobal(VERSION, {
  core: () => import('./core/init.js'),
  gpu: () => import('./core/gpuDetector.js'),
  viewport: () => import('./core/viewport.js'),
  tools: () => import('./tools/toolManager.js'),
  ai: () => import('./ai/aiModule.js'),
  compute: () => import('./compute/gpuCompute.js'),
  // [UPDATE] Заменяем старый largeDatasets на наш новый Enterprise-менеджер
  data: () => import('./managers/DatasetManager.js'),
  rendering: () => import('./rendering/cinematicRendering.js'),
  presets: () => import('./core/windowPresets.js'),
  // [СРХ Этап 1] Импорт и показ RT Structure Set (RTSTRUCT)
  rtstruct: () => import('./io/rtStructModule.js'),
});

// ══════════════════════════════════════════════════════════════
// ИЗОЛЯЦИЯ И SharedArrayBuffer
// ══════════════════════════════════════════════════════════════

const ISOLATION_WARN_KEY = 'mikhmel.isolationWarned';
const ISOLATION_WARN_EVERY_MS = 7 * 24 * 60 * 60 * 1000;   // раз в неделю, не чаще

async function _warnAboutIsolation(support) {
  const isolated = (typeof crossOriginIsolated !== 'undefined') ? crossOriginIsolated : undefined;
  const haveSAB = !!support.sharedArrayBuffer;
  if (haveSAB && isolated !== false) return;   // всё на месте — молчим

  // [10.1.0] В настольной сборке общая память включена напрямую флагом
  // оболочки, а изоляция источника при этом остаётся выключенной: Chromium не
  // выдаёт её собственным схемам вроде app://. То есть crossOriginIsolated
  // здесь всегда false, и по нему судить нельзя — важно наличие самой памяти.
  if (haveSAB && window.MikhmelDesktop?.isDesktop) return;
  
  // [NEW] Если это desktop-сборка, SAB доступен через Electron
  if (IS_DESKTOP_BUILD && haveSAB) return;

  // Служебный поток ещё не управляет страницей? Значит заголовки просто не
  // успели появиться: при следующем открытии они будут. Молчим.
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && !navigator.serviceWorker.controller) {
        log.info('[Изоляция] служебный поток ещё не управляет страницей — заголовки появятся при следующем открытии');
        return;
      }
    }
  } catch (e) { /* спросить не удалось — рассуждаем дальше */ }

  log.warn('[Изоляция] SharedArrayBuffer недоступен: объём собирается медленнее');

  try {
    const last = Number(localStorage.getItem(ISOLATION_WARN_KEY) || 0);
    if (Date.now() - last < ISOLATION_WARN_EVERY_MS) return;
    localStorage.setItem(ISOLATION_WARN_KEY, String(Date.now()));
  } catch (e) { /* хранилище закрыто — покажем один раз за сеанс */ }

  showToast('Объём будет собираться медленнее обычного: браузер не выдал общую память. '
    + 'На работу это не влияет, только на скорость.', 'warning', 6000);
}

/**
 * [10.1.4] Срок годности сборки — до всего остального.
 *
 * Показываем окно на весь экран и НЕ поднимаем движок: смысл рубежа в том,
 * чтобы по устаревшей сборке не работали. Данные при этом не трогаются —
 * подробности в src/core/buildExpiry.js.
 */
function _showExpiryWall(msg, рубеж = EXPIRY_UTC) {
  const wall = document.createElement('div');
  wall.setAttribute('role', 'alertdialog');
  wall.style.cssText = 'position:fixed;inset:0;z-index:2147483647;display:flex;'
    + 'align-items:center;justify-content:center;background:#0a0e16;padding:24px;'
    + "font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#cfe0ef;";
  const card = document.createElement('div');
  card.style.cssText = 'max-width:560px;text-align:center;line-height:1.6;';
  const h = document.createElement('div');
  h.style.cssText = 'font-size:22px;font-weight:700;color:#fff;margin-bottom:14px;';
  h.textContent = msg.title;
  // [12.0.40] Вход автора в перенос срока — и здесь: после истечения
  // программа не запускается, до Диагностики не дойти ни с компьютера, ни с
  // телефона. Семь касаний заголовка или удержание три секунды.
  import('./ui/expiryShift.js').then((m) => m.привязатьДолгоеНажатие(h)).catch(() => { /* без входа — только ключ */ });
  const t = document.createElement('div');
  t.style.cssText = 'font-size:15px;color:#9fb3cf;';
  t.textContent = msg.text;
  card.append(h, t);

  // Поле для ключа продления. Ошибку показываем текстом рядом, а не тостом:
  // тосты живут в интерфейсе, которого на этой стене нет.
  const row = document.createElement('div');
  row.style.cssText = 'margin-top:22px;display:flex;gap:8px;justify-content:center;flex-wrap:wrap;';
  const inp = document.createElement('input');
  inp.type = 'password';
  inp.autocomplete = 'off';
  inp.placeholder = 'Ключ продления';
  inp.setAttribute('aria-label', 'Ключ продления');
  inp.style.cssText = 'flex:1 1 260px;max-width:340px;padding:9px 12px;border-radius:8px;'
    + 'border:1px solid #2a3546;background:#131a26;color:#cfe0ef;font-size:14px;';
  const go = document.createElement('button');
  go.textContent = 'Продолжить';
  go.style.cssText = 'padding:9px 18px;border-radius:8px;border:1px solid #3b82f6;'
    + 'background:#1d4ed8;color:#fff;font-size:14px;font-weight:600;cursor:pointer;';
  const err = document.createElement('div');
  err.style.cssText = 'margin-top:10px;font-size:13px;color:#f87171;min-height:18px;';
  row.append(inp, go);
  card.append(row, err);

  const submit = async () => {
    go.disabled = true;
    const okKey = await verifyUnlock(inp.value);
    if (!okKey) {
      err.textContent = 'Ключ не подошёл.';
      go.disabled = false;
      inp.select();
      return;
    }
    // Метка привязывается к ДЕЙСТВУЮЩЕМУ рубежу: если срок переносили, ключ
    // должен снимать именно тот рубеж, который сейчас запер программу.
    try { localStorage.setItem(unlockKeyFor(рубеж), '1'); } catch (e) { /* приватный режим */ }
    location.reload();
  };
  go.addEventListener('click', submit);
  inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });

  wall.appendChild(card);
  document.body.appendChild(wall);
  setTimeout(() => inp.focus(), 50);
}

async function main() {
  log.debug(`${APP_NAME} v${VERSION} (${BUILD_MODE} build)`);
  emit('app:starting', { version: VERSION, buildMode: BUILD_MODE });

  // Проверка срока — самой первой: поднимать движок ради того, чтобы тут же
  // закрыть его окном, значит зря съесть память и время.
  try {
    // [10.19.9] Рубеж берём действующий: встроенный или служебно перенесённый
    // (что позже). Перенос — только вперёд и не дальше потолка, см. buildExpiry.
    // [12.0.40] Дата из srok.js записана с ошибкой — не запираем никого, но
    // говорим громко: test.cmd на такой дате тоже падает.
    if (!СРОК_ЗАПИСАН_ВЕРНО) log.error('[Срок] в srok.js дата записана с ошибкой — рубеж не действует. Запустите srok.cmd');
    const рубеж = действующийСрок((k) => { try { return localStorage.getItem(k); } catch (e) { return null; } });
    const state = checkExpiry(Date.now(), рубеж);
    if (рубеж !== EXPIRY_UTC) log.info(`[Срок] действует перенесённый рубеж: ${state.dateText}`);
    // Ключ уже вводили для ЭТОГО рубежа — не спрашиваем снова.
    let unlocked = false;
    try { unlocked = localStorage.getItem(unlockKeyFor(рубеж)) === '1'; } catch (e) { /* */ }
    if (state.expired && unlocked) {
      log.warn(`[Срок] сборка ${VERSION} просрочена, но продлена ключом`);
    } else if (state.expired) {
      log.warn(`[Срок] сборка ${VERSION} просрочена (до ${state.dateText})`);
      _showExpiryWall(expiryMessage(state, VERSION), рубеж);
      return;
    }
    if (state.warn) {
      log.info(`[Срок] осталось дней: ${state.daysLeft}`);
      const m = expiryMessage(state, VERSION);
      setTimeout(() => { try { showToast(`${m.title}. ${m.text}`, 'warning', 9000); } catch (e) { /* */ } }, 4000);
    }
  } catch (e) { log.debug('[Срок]', e.message); }

  // [10.0.2] Измерить экран ДО первой отрисовки оболочки.
  //
  // Оболочка меряется --app-h, и если переменной ещё нет, первый кадр уйдёт
  // по запасному 100dvh — то есть по той самой высоте, которая на мобильных
  // браузерах врёт. Ставим значение до всего остального: модуль ничего не
  // импортирует и ничего не ждёт.
  try { initScreenFit(); } catch (e) { log.debug('[screenFit]', e.message); }

  // [10.0.3] Глобальные ловушки исключений — до всего остального, чтобы
  // поймать в том числе падения на старте.
  try { setupGlobalErrorHandlers(); } catch (e) { log.debug('[errorBoundary]', e.message); }

  const support = checkBrowserSupport();
  const loaderEl = document.getElementById(DOM_IDS.LOADER);
  const appEl = document.getElementById(DOM_IDS.APP);

  // [10.4.0] Раньше здесь программа заканчивалась: нет WebGL2 — красная
  // английская надпись и `return`. На старом ноутбуке (Lenovo B570, Intel HD
  // 3000) это тупик: врач видит стену и не знает, что делать.
  //
  // А делать есть что. Cornerstone умеет рисовать срезы ПРОЦЕССОРОМ, и этот
  // путь в init.js был написан давно (`useCPURendering`). До него просто не
  // доходило — проверка обрывала запуск раньше. Объём и правда не построить,
  // видеокарту не подделаешь; но стопка срезов, листание, окно яркости и
  // измерения — то, ради чего программу и открывают, — работают.
  //
  // Поэтому теперь: определяем, на что машина способна, говорим об этом
  // по-русски и продолжаем в посильном режиме. Само правило — в
  // core/renderMode.js, отдельным файлом без импортов, чтобы его можно было
  // проверить тестом и чтобы оно не расползлось по коду.
  const _mode = decideRenderMode({
    webgl2: support.webgl2,
    webgpu: support.webgpu,
    renderer: _probeRendererName(),
  });
  window.__renderMode = _mode;
  log.info(`[Запуск] ${describeRenderMode(_mode)}`);
  if (_mode.kind !== 'full') _showRenderModeNotice(_mode);

  // [10.0.4] Про SharedArrayBuffer — по делу и один раз, а не каждый запуск.
  //
  // Что было. Предупреждение показывалось при каждом открытии, по-английски, и
  // требовало «проверить заголовки COOP/COEP» — совет администратору сервера,
  // а не врачу.
  //
  // И чаще всего оно было ПРЕЖДЕВРЕМЕННЫМ. Нужные заголовки выставляет наш же
  // служебный поток (sw.js добавляет их к навигационному ответу). При первом
  // открытии он ещё не управляет страницей — изоляции нет, буфера нет,
  // предупреждение вылезает. После перезагрузки всё в порядке, но человек
  // этого не знает и видит тревогу на ровном месте. Подробности — в
  // _warnAboutIsolation.
  _warnAboutIsolation(support);

  try {
    // 1. Инициализация ядра (Cornerstone)
    const { gpuInfo } = await initCornerstone();
    emit('cornerstone:ready', { gpuInfo });

    // ==========================================
    // [GOD-TIER] ИНИЦИАЛИЗАЦИЯ ЗАЩИТЫ ПАМЯТИ
    // ==========================================
    memoryGuardian.start();
    
    // Подключаем UI-уведомления к событиям памяти
    memoryGuardian.addEventListener('memory-pressure', (e) => {
      // [10.3.2] Сторож памяти пишет в журнал, а не на экран. Он срабатывает
      // как раз во время большого исследования — то есть сообщение ложилось
      // поверх снимка в момент работы, и не по разу. Ответить на него нечем:
      // очистку сторож уже сделал сам, до сообщения.
      log.warn(`[System] Высокая нагрузка на ОЗУ (${(e.detail.ratio * 100).toFixed(1)}%) — очищаю старые данные`);
    });

    memoryGuardian.addEventListener('memory-cleared', (e) => {
      log.info(`[System] 🧹 Страж памяти выгрузил ${e.detail.freedCount} слоев из RAM.`);
    });
    // ==========================================

    // 2. Опциональные модули (Streaming)
    try {
      const { initStreamingManager } = await import('./core/streamingManager.js');
      initStreamingManager();
    } catch (e) { 
      log.warn('[init] StreamingManager failed to load. Falling back to default loader.', e.message); 
    }

    showStartupDiagnostic();
    hookFileInputs();

    // 3. Регистрация API Viewport ДО инициализации UI
    const vp = await import('./core/viewport.js');
    registerAPIs({
      // [10.19.5] Замер последней загрузки и сверка «файлов ↔ срезов» — наружу.
      // Плашка больше не выскакивает после каждой нормальной загрузки, поэтому
      // и сверка, и время должны быть доступны спросом: кнопкой в
      // «Диагностике» и здесь — для проверок и разбора на телефоне.
      lastLoadReport: async () => (await import('./core/loadReport.js')).последнийЗамер(),
      showLoadReport: async () => (await import('./ui/fileOps.js')).показатьЗамерЗагрузки(),
      // [10.19.1] Показ сообщения — наружу. На телефоне консоли нет, а
      // осмотру кнопок нужно уметь поднять тост, чтобы проверить, что он не
      // глушит кнопки под собой (эта беда там и нашлась).
      showToast,
      // [10.19.14] Хранилище наружу: круг «сохранили разметку → прочитали обратно»
      // иначе проверить нечем — 55 функций хранилища не покрывал ни один тест.
      saveAnnotation: vp.saveAnnotation,
      loadAnnotations: vp.loadAnnotations,
      deleteAnnotation: vp.deleteAnnotation,
      // [10.19.14] Перенос для МРТ по гистограмме серии.
      применитьМРПеренос: vp.применитьМРПеренос,
      toggleCinematic: vp.toggleCinematic,
      setCinematicPreset: vp.setCinematicPreset,
      _getCinematicRenderer: vp.getCinematicRenderer,
      // [NEW] Делаем datasetManager глобально доступным через API
      datasetManager: datasetManager,
      // [PREMIUM] Premium Features API
      premium: () => import('./ui/premiumFeatures.js')
    });
    // [12.0.20] В событие уходят ПРИЗНАКИ, а не функции. Шина при строгой
    // неизменяемости клонирует полезную нагрузку через structuredClone, а
    // функция не клонируется — на каждом запуске в журнал падали три
    // предупреждения «structuredClone failed». Сами функции никуда не делись:
    // они зарегистрированы в реестре API строкой выше (toggleCinematic,
    // setCinematicPreset, _getCinematicRenderer) и берутся через getAPI.
    // Слушателей у этого события нет ни одного — проверено по всему дереву.
    emit('cinematic:available', { доступен: true, через: 'API' });

    // 4. Инициализация основного UI
    initUI();
    emit('ui:ready');

    // ★ [ENTERPRISE] Монтируем панель с 80+ кнопками для всех enterprise-фич
    try { разложитьРазделы(); } catch (epErr) { console.error('[Разделы] раскладка не удалась:', epErr); }
    // Toolbar button → toggle panel directly (no data-proxy, floating button hidden on desktop)
    // [10.19.47] Кнопка btn-enterprise-panel больше ничего не открывает —
    // панели нет; разделы разложены по вкладкам сайдбара и окнам.

    // ★ [PREMIUM] Welcome Screen, PiP, Voice Status, Gestures, Timeline, Night Mode, Breadcrumb
    try { initPremiumFeatures(); } catch(pfErr) { console.error('[PremiumFeatures] Init failed:', pfErr); }

    // ★ [TF EDITOR] Direct button binding — independent of sidebarBindings
    try {
      const tfBtn = document.getElementById('btn-tf-editor');
      if (tfBtn) {
        // [12.0.51] Открытие — через сам редактор (toggleTFEditor), а не
        // подстановкой стиля снаружи. Прежний обработчик показывал окно сам,
        // и редактор об этом не знал (_visible оставался false): крестик «✕»
        // первым нажатием окно НЕ закрывал, а «открывал» повторно — закрывал
        // только второй щелчок; кривые при открытии не перерисовывались.
        tfBtn.addEventListener('click', () => {
          import('./core/tfEditor.js')
            .then((м) => м.toggleTFEditor())
            .catch((e) => console.error('[TFEditor] открытие:', e));
        });
        console.log('[TFEditor] Button bound in main.js');
      } else {
        console.warn('[TFEditor] #btn-tf-editor NOT in DOM');
      }
    } catch(tfErr) { console.error('[TFEditor] Bind failed:', tfErr); }

    // ★ [3D ROTATION] Left sidebar rotation buttons
    try {
      const rotH = document.getElementById('btn-rotate-h');
      const rotV = document.getElementById('btn-rotate-v');
      const rotStop = document.getElementById('btn-rotate-stop');
      if (rotH) rotH.addEventListener('click', () => {
        const vpId = find3DViewportId();
        if (!vpId) { showToast('❌ Нет 3D viewport', 'error', 1500); return; }
        if (isAutoRotating()) { stopAutoRotate(); }
        stopAutoSpin(); // [10.19.31] два двигателя не крутят одну камеру
        startAutoRotate(vpId, { degreesPerSecond: 30, axis: 'horizontal' });
        showToast('🔄 Горизонтальное вращение', 'info', 1000);
      });
      if (rotV) rotV.addEventListener('click', () => {
        const vpId = find3DViewportId();
        if (!vpId) { showToast('❌ Нет 3D viewport', 'error', 1500); return; }
        if (isAutoRotating()) { stopAutoRotate(); }
        stopAutoSpin(); // [10.19.31] два двигателя не крутят одну камеру
        startAutoRotate(vpId, { degreesPerSecond: 30, axis: 'vertical' });
        showToast('🔃 Вертикальное вращение', 'info', 1000);
      });
      if (rotStop) rotStop.addEventListener('click', () => {
        stopAutoRotate();
        showToast('⏹ Вращение остановлено', 'info', 1000);
      });
    } catch(rotErr) { console.error('[Rotation] Bind failed:', rotErr); }

    // ★ [3D ROTATION] Mobile buttons (same logic, different IDs)
    try {
      const mRotH = document.getElementById('m-rotate-h');
      const mRotV = document.getElementById('m-rotate-v');
      const mRotStop = document.getElementById('m-rotate-stop');
      if (mRotH) mRotH.addEventListener('click', () => {
        const vpId = find3DViewportId();
        if (!vpId) return;
        if (isAutoRotating()) stopAutoRotate();
        startAutoRotate(vpId, { degreesPerSecond: 30, axis: 'horizontal' });
      });
      if (mRotV) mRotV.addEventListener('click', () => {
        const vpId = find3DViewportId();
        if (!vpId) return;
        if (isAutoRotating()) stopAutoRotate();
        startAutoRotate(vpId, { degreesPerSecond: 30, axis: 'vertical' });
      });
      if (mRotStop) mRotStop.addEventListener('click', () => stopAutoRotate());
    } catch(e) { /* safe */ }

    // ★ [BOTTOMBAR] GPU/Telemetry click handlers
    try {
      const { getGPUInfo, getSliceLimit, getRecommendedBatchSize } = await import('./core/gpuDetector.js');
      
      document.getElementById('bottombar-gpu')?.addEventListener('click', () => {
        const g = getGPUInfo();
        showToast(`GPU: ${g.renderer || '?'}\nTier: ${g.tier?.label || '?'}\nVRAM: ~${g.vramEstimateMB || '?'} MB\nWebGL2: ${g.webgl2 ? '✅' : '❌'}\nWebGPU: ${g.webgpu ? '✅' : '❌'}\nMobile: ${g.isMobile ? 'Да' : 'Нет'}\nCores: ${g.cores || '?'}`, 'info', 8000);
      });
      
      document.getElementById('bottombar-webgpu')?.addEventListener('click', async () => {
        try {
          const { getLibraryVersions } = await import('./core/init.js');
          const v = getLibraryVersions();
          showToast(`Cornerstone3D: ${v.cornerstone}\nTools: ${v.tools}\nDICOM Loader: ${v.dicomLoader}`, 'info', 5000);
        } catch(e) { showToast('Версии недоступны', 'info', 1500); }
      });
      
      document.getElementById('bottombar-cache')?.addEventListener('click', async () => {
        try {
          const { getSystemDiagnostics } = await import('./core/init.js');
          const d = getSystemDiagnostics();
          showToast(`Cache: ${d.cache.currentSizeMB}/${d.cache.maxSizeMB} MB (${d.cache.usagePercent}%)\nMax slices: ${getSliceLimit()}\nBatch: ${getRecommendedBatchSize()}`, 'info', 5000);
        } catch(e) { showToast('❌ ' + e.message, 'error', 2000); }
      });
      
      document.getElementById('bottombar-diag')?.addEventListener('click', async () => {
        // [10.19.44] Было тостом — и в сборке Электрона исчезало начисто:
        // текст сводки перечисляет возможности браузера, среди них
        // sharedArrayBuffer, а заслон болтовни ловил это слово (правило про
        // «браузер не выдал общую память»). В браузере без кросс-изоляции
        // слова не было — кнопка «работала», в Электроне оно есть — тишина.
        // Заслон научен пропускать прямые ответы (toastPolicy 10.19.44), а
        // сама диагностика переехала в окно: её нужно читать целиком и
        // копировать, чего тост не умеет ни на телефоне, ни на десктопе.
        const { собратьДиагностику, показатьДиагностику } = await import('./ui/diagnosticsPanel.js');
        try { показатьДиагностику(await собратьДиагностику()); }
        catch (e) { showToast('Диагностика недоступна: ' + e.message, 'error', 3000); }
      });
    } catch(e) { console.warn('[Bottombar] GPU handlers failed:', e.message); }

    // [10.19.9] Оговорка «Не для диагностики» в нижней строке: по нажатию —
    // полный текст. Стоит здесь, а не в отдельном модуле, потому что нижняя
    // строка собирается тут же и других обработчиков у неё нет.
    try {
      const надпись = document.getElementById('not-for-diagnosis');
      if (надпись) {
        const показать = async () => {
          try {
            const { ПОЛНЫЙ } = await import('./core/disclaimer.js');
            showToast(ПОЛНЫЙ, 'warning', 25000);
          } catch (e) { /* модуль не загрузился — надпись всё равно на месте */ }
        };
        надпись.addEventListener('click', показать);
        надпись.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); показать(); }
        });
      }
    } catch (e) { /* без оговорки в футере программа всё равно работает */ }

    // 5. Переключение экранов загрузки
    if (loaderEl) loaderEl.style.display = 'none';
    if (appEl) appEl.style.display = 'flex';

    log.debug('='.repeat(50));
    log.debug(`${APP_NAME} v${VERSION} — Ready (${BUILD_MODE})`);
    log.debug(`GPU: ${gpuInfo.tier?.label || 'Unknown'} (${gpuInfo.renderer || 'Unknown'})`);
    log.debug(`WebGPU: ${gpuInfo.webgpu ? '✓' : '✗'}`);
    log.debug(`VRAM: ~${gpuInfo.vramEstimateMB || 'N/A'}MB | Max slices: ${gpuInfo.recommendedSlices || 'N/A'}`);
    log.debug(`Smart Memory Guardian: ${memoryGuardian.hasMemoryAPI ? 'Active ✓' : 'Passive (Fallback)'}`);
    log.debug('='.repeat(50));

    // [10.3.2] Тоста «v… — Ready» нет: номер версии написан на заставке, а
    // сообщение о собственном запуске врачу ничего не даёт.

    emit('app:ready', { version: VERSION, gpuInfo, buildMode: BUILD_MODE });
    window.dispatchEvent(new Event('mikhmel-ready'));

    initIndexBindings();
    // [10.19.11] Косые реформаты: три ползунка наклона теперь поворачивают
    // камеру активного окна, а не только подпись рядом с собой.
    try { initOblique(); } catch (e) { log.debug('[Наклон]', e.message); }
    try { initLvEf(); } catch (e) { log.debug('[ФВ по маске]', e.message); }
    try { initEchoReg(); } catch (e) { log.debug('[Эхо-совмещение]', e.message); }
    try { initEchoSimpson(); } catch (e) { log.debug('[Симпсон]', e.message); }
    try { initCPR(); } catch (e) { log.debug('[CPR]', e.message); }
    try { initQuantTools(); } catch (e) { log.debug('[Количественные]', e.message); }
    try { initPetPerfusionTools(); } catch (e) { log.debug('[ПЭТ/перфузия]', e.message); }
    try { initWorklist(); } catch (e) { log.debug('[Список]', e.message); }

    // 6. Инициализация подмодулей ПО ФАЗАМ.
    //
    // [10.0.3] Раньше здесь был один плоский список и Promise.allSettled: всё
    // стартовало одновременно. При этом в комментарии стояло «после
    // recoveryReport», хотя одновременный запуск такого порядка не даёт —
    // договорённость была на бумаге, а не в коде. Теперь очередь настоящая:
    // следующая фаза начинается, когда закончилась предыдущая.
    //
    // Внутри фазы модули по-прежнему идут разом: они независимы, и ждать
    // друг друга им незачем.
    const phases = [
      {
        name: 'Ядро',
        modules: [
          { name: 'app', fn: initApp },
          { name: 'viewportResize', fn: initViewportResize },
          { name: 'sidebarResize', fn: initSidebarResize },
        ],
      },
      {
        name: 'Интерфейс',
        modules: [
          { name: 'mobileTopbar', fn: initMobileTopbar },
          { name: 'mobileNav', fn: initMobileNav },
          { name: 'contextMenu', fn: initContextMenu },
          { name: 'inlineHelp', fn: initInlineHelp },
        ],
      },
      {
        name: 'Возможности',
        modules: [
          // [10.2.0] Имя фазы видно на заставке. Установку PWA мы убрали в
          // 10.1.4, модуль остался только ради служебного потока — так и пишем.
          { name: 'офлайн-режим', fn: initPWA },
          { name: 'profile', fn: async () => {
              await loadProfile();
              applyProfileToUI();
              registerAPIs({ setProfile, showProfileSelector });
              // [10.19.31] Кнопка «Профиль врача» была МЕРТВА: profileManager
              // лишь рисовал на ней имя, клика не слушал никто, а дубль в
              // Enterprise убрали ещё в 10.3.5, доверившись ей. Оживлена.
              document.getElementById('btn-profile')?.addEventListener('click', () => showProfileSelector());
            },
          },
          { name: 'mobileStability', fn: initMobileStability },
          { name: 'proFeatures', fn: initProFeaturesBindings },
        ],
      },
      {
        name: 'Лучевая терапия',
        modules: [
          { name: 'rtstruct', fn: initRTStruct },
          { name: 'registration', fn: initRegistration },
          { name: 'rtdose', fn: initRTDose },
          { name: 'dvh', fn: initDVH },
          { name: 'isocenter', fn: initIsocenter },
          { name: 'protocolGate', fn: initProtocolGate },
        ],
      },
      {
        name: 'Служебное',
        modules: [
          { name: 'credits', fn: initCredits },
          // Спрайт нужен раньше, чем кто-то попросит иконку.
          { name: 'icons', fn: injectIconSprite },
          { name: 'recoveryReport', fn: initRecoveryReport },
        ],
      },
      {
        name: 'Возврат сеанса',
        modules: [
          // Отдельной фазой, и это не педантизм: recoveryReport определяет,
          // выгружал ли браузер вкладку, а sessionRestore на основании этого
          // решает, предлагать ли вернуться. Запуск вперемешку давал гонку —
          // предложение то появлялось, то нет.
          { name: 'sessionRestore', fn: initSessionRestore },
        ],
      },
    ];

    for (const phase of phases) {
      const results = await Promise.allSettled(phase.modules.map(m => m.fn()));
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          ErrorBoundary.capture(r.reason, `Модуль ${phase.name}/${phase.modules[i].name}`);
        }
      });
    }

    // 7. Тестовый модуль (только в режиме разработки)
    if (IS_DEV) {
      try { 
        await import('./tests/viewportFunctionsTest.js'); 
        log.debug('[dev] viewportFunctionsTest mounted');
      } catch (e) { 
        log.debug('[init] viewportTest:', e.message); 
      }
    }

  } catch (error) {
    console.error('[Main] Initialization failed:', error);
    log.error('Initialization failed:', error);
    showToast(`Init error: ${error.message}`, 'error', 10000);
    emit('app:error', { error });
    
    if (loaderEl) {
      loaderEl.style.display = 'flex';
      loaderEl.innerHTML = `<div style="color:red;">Critical Error: ${escapeHtml(error.message)}</div>`;
    }
  }
}

document.addEventListener('DOMContentLoaded', main);
// ══════════════════════════════════════════════════════════════
// [10.4.0] Режим отрисовки: имя видеокарты и объяснение для человека
// ══════════════════════════════════════════════════════════════

/** Имя видеокарты глазами браузера. Нужно, чтобы отличить настоящую от программной. */
function _probeRendererName() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    if (!gl) return '';
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    return String(dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER) || '');
  } catch (e) { return ''; }
}

/**
 * Окно «почему так и что можно сделать». Показывается один раз за сеанс и
 * закрывается кнопкой — работать оно не мешает.
 */
function _showRenderModeNotice(mode) {
  try {
    if (document.getElementById('render-mode-notice')) return;
    const wrap = document.createElement('div');
    wrap.id = 'render-mode-notice';
    wrap.setAttribute('role', 'dialog');
    wrap.style.cssText = 'position:fixed;inset:0;z-index:30050;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.72);padding:16px;';
    const advice = (mode.advice || []).map((a) => `<li style="margin:6px 0;">${escapeHtml(a)}</li>`).join('');
    wrap.innerHTML = `
      <div style="max-width:520px;background:#12161f;border:1px solid rgba(150,180,225,0.35);border-radius:14px;padding:20px 22px;color:#dbe6f5;font-size:13px;line-height:1.5;box-shadow:0 12px 48px rgba(0,0,0,0.6);">
        <div style="font-size:15px;font-weight:700;color:#96d2ff;margin-bottom:10px;">${escapeHtml(mode.title)}</div>
        <div style="margin-bottom:12px;">${escapeHtml(mode.why)}</div>
        ${advice ? `<div style="font-size:12px;color:#a9bad0;margin-bottom:6px;">Что можно сделать:</div><ul style="margin:0 0 14px 18px;padding:0;font-size:12px;color:#a9bad0;">${advice}</ul>` : ''}
        <button id="render-mode-ok" style="width:100%;padding:10px;border-radius:9px;border:1px solid rgba(150,180,225,0.5);background:rgba(30,42,64,0.95);color:#e6eefb;font-size:13px;font-weight:600;cursor:pointer;">Понятно, продолжить</button>
      </div>`;
    document.body.appendChild(wrap);
    wrap.querySelector('#render-mode-ok')?.addEventListener('click', () => wrap.remove());
  } catch (e) { log.debug('[renderMode] окно не показалось:', e.message); }
}